import { useEffect, useRef, useState } from 'react'

import { prepareGuideTourForcedScroll } from '../utils/targets'
import {
  measureGuideTourTarget,
  type GuideTourTargetMeasurementState,
} from './guideTourTargetMeasurement'
import {
  ignoreRejectedGuideTourPromise,
  requestGuideTourProgrammaticScroll,
  revealGuideTourBoundsAfterScroll,
} from './guideTourTargetScrollFlow'
import {
  GUIDE_TOUR_SCROLL_POLL_INTERVAL_MS,
  GUIDE_TOUR_TARGET_SCROLL_BEHAVIOR,
  scrollGuideTourTargetIntoView,
  syncGuideTourTargetInView,
  isGuideTourTargetVisibleInViewport,
} from './targetVisibility'
import {
  findGuideTourTargetElement,
  getGuideTourTargetBounds,
  type SpotlightBounds,
} from './utils'

export const GUIDE_TOUR_TARGET_SCROLL_MAX_ATTEMPTS = 3

export type UseGuideTourTargetOptions = {
  enabled?: boolean
  /** Element id to scroll into view before revealing the spotlight. */
  scrollTargetId?: string | null
  /** When false, only reveal the spotlight after the user scrolls the target into view. */
  forceScrollToTarget?: boolean
  activationKey?: string | null
}

export const useGuideTourTarget = (
  targetId: string | null,
  {
    enabled = true,
    scrollTargetId = null,
    forceScrollToTarget = false,
    activationKey = null,
  }: UseGuideTourTargetOptions = {}
): SpotlightBounds | null => {
  const [bounds, setBounds] = useState<SpotlightBounds | null>(null)
  const scrolledActivationKeyRef = useRef<string | null>(null)
  const scrollAttemptRef = useRef(0)
  const pendingScrollRevealRef = useRef(false)
  const isAwaitingRevealRef = useRef(false)
  const revealAbortRef = useRef<{ aborted: boolean } | null>(null)
  const scrollRequestIdRef = useRef(0)
  const hasRevealedBoundsRef = useRef(false)
  const lastRevealedBoundsRef = useRef<SpotlightBounds | null>(null)
  const awaitingInitialStabilityRef = useRef(false)
  const initialStabilityDoneRef = useRef(false)

  useEffect(() => {
    scrolledActivationKeyRef.current = null
    scrollAttemptRef.current = 0
    pendingScrollRevealRef.current = false
    isAwaitingRevealRef.current = false
    hasRevealedBoundsRef.current = false
    lastRevealedBoundsRef.current = null
    awaitingInitialStabilityRef.current = false
    initialStabilityDoneRef.current = false
    scrollRequestIdRef.current += 1

    if (revealAbortRef.current) {
      revealAbortRef.current.aborted = true
      revealAbortRef.current = null
    }

    if (!enabled || !targetId) {
      setBounds(null)
      return undefined
    }

    let scrollSettleTimer = 0
    let scrollPollTimer = 0
    const scrollRequestId = scrollRequestIdRef.current

    const isScrollRequestStale = () =>
      scrollRequestIdRef.current !== scrollRequestId

    const stopScrollPolling = () => {
      window.clearInterval(scrollPollTimer)
      scrollPollTimer = 0
    }

    const startScrollPolling = () => {
      stopScrollPolling()
      scrollPollTimer = window.setInterval(
        scheduleMeasure,
        GUIDE_TOUR_SCROLL_POLL_INTERVAL_MS
      )
    }

    const commitBounds = (nextBounds: SpotlightBounds) => {
      hasRevealedBoundsRef.current = true
      lastRevealedBoundsRef.current = nextBounds
      setBounds(nextBounds)
    }

    const runProgrammaticScroll = (element: HTMLElement) => {
      if (isScrollRequestStale()) {
        return
      }

      const scrollElement = scrollTargetId
        ? (findGuideTourTargetElement(scrollTargetId) ?? element)
        : element

      prepareGuideTourForcedScroll()
      scrollGuideTourTargetIntoView(
        scrollElement,
        GUIDE_TOUR_TARGET_SCROLL_BEHAVIOR,
        { forcePageScroll: forceScrollToTarget }
      )
      startScrollPolling()
    }

    const requestProgrammaticScrollForElement = (element: HTMLElement) => {
      requestGuideTourProgrammaticScroll({
        element,
        scrollTargetId,
        activationKey,
        scrollRequestId,
        isScrollRequestStale,
        scrolledActivationKey: scrolledActivationKeyRef.current,
        scrollAttempt: scrollAttemptRef.current,
        maxScrollAttempts: GUIDE_TOUR_TARGET_SCROLL_MAX_ATTEMPTS,
        onScrollAttemptScheduled: ({
          scrolledActivationKey,
          scrollAttempt,
          pendingScrollReveal,
        }) => {
          scrolledActivationKeyRef.current = scrolledActivationKey
          scrollAttemptRef.current = scrollAttempt
          pendingScrollRevealRef.current = pendingScrollReveal
        },
        runProgrammaticScroll,
      })
    }

    const keepTargetInViewWhileLocked = (element: HTMLElement) => {
      syncGuideTourTargetInView(element, {
        forcePageScroll: forceScrollToTarget,
      })

      const syncedBounds = getGuideTourTargetBounds(element)
      commitBounds(syncedBounds)

      const syncedVisible = isGuideTourTargetVisibleInViewport(syncedBounds, {
        width: window.innerWidth,
        height: window.innerHeight,
      })

      if (!syncedVisible) {
        startScrollPolling()
        return
      }

      pendingScrollRevealRef.current = false
      isAwaitingRevealRef.current = false
    }

    const scheduleRevealAfterScroll = (abort: { aborted: boolean }) => {
      if (revealAbortRef.current) {
        revealAbortRef.current.aborted = true
      }

      revealAbortRef.current = abort

      revealGuideTourBoundsAfterScroll({
        targetId,
        abort,
        isScrollRequestStale,
        setBounds,
        onRevealComplete: settledBounds => {
          pendingScrollRevealRef.current = false
          isAwaitingRevealRef.current = false
          revealAbortRef.current = null
          commitBounds(settledBounds)
        },
        requestProgrammaticScroll: requestProgrammaticScrollForElement,
      }).catch(ignoreRejectedGuideTourPromise)
    }

    const buildMeasurementState = (): GuideTourTargetMeasurementState => ({
      hasRevealedBounds: hasRevealedBoundsRef.current,
      lastRevealedBounds: lastRevealedBoundsRef.current,
      pendingScrollReveal: pendingScrollRevealRef.current,
      isAwaitingReveal: isAwaitingRevealRef.current,
      initialStabilityDone: initialStabilityDoneRef.current,
      awaitingInitialStability: awaitingInitialStabilityRef.current,
      scrollAttempt: scrollAttemptRef.current,
    })

    const applyMeasurementState = (state: GuideTourTargetMeasurementState) => {
      if (state.hasRevealedBounds) {
        hasRevealedBoundsRef.current = true
      }
      if (state.lastRevealedBounds) {
        lastRevealedBoundsRef.current = state.lastRevealedBounds
      }
      pendingScrollRevealRef.current = state.pendingScrollReveal
      isAwaitingRevealRef.current = state.isAwaitingReveal
      initialStabilityDoneRef.current = state.initialStabilityDone
      awaitingInitialStabilityRef.current = state.awaitingInitialStability
      scrollAttemptRef.current = state.scrollAttempt
    }

    const scheduleMeasure = () => {
      const state = buildMeasurementState()
      measureGuideTourTarget({
        targetId,
        forceScrollToTarget,
        scrollTargetId,
        state,
        getHasRevealedBounds: () => hasRevealedBoundsRef.current,
        getLastRevealedBounds: () => lastRevealedBoundsRef.current,
        setBounds,
        startScrollPolling,
        stopScrollPolling,
        keepTargetInViewWhileLocked,
        requestProgrammaticScroll: requestProgrammaticScrollForElement,
        scheduleRevealAfterScroll,
        isScrollRequestStale,
        maxScrollAttempts: GUIDE_TOUR_TARGET_SCROLL_MAX_ATTEMPTS,
        rescheduleMeasure: scheduleMeasure,
        persistMeasurementState: applyMeasurementState,
      })
      applyMeasurementState(state)
    }

    scheduleMeasure()

    const element = findGuideTourTargetElement(targetId)
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleMeasure)
        : null

    if (element) {
      resizeObserver?.observe(element)
    }

    resizeObserver?.observe(document.body)

    const layoutObserver = new MutationObserver(scheduleMeasure)
    layoutObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    const onViewportChange = () => {
      scheduleMeasure()
      window.clearTimeout(scrollSettleTimer)
      scrollSettleTimer = window.setTimeout(scheduleMeasure, 80)
    }

    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)

    return () => {
      window.clearTimeout(scrollSettleTimer)
      stopScrollPolling()
      layoutObserver.disconnect()

      if (revealAbortRef.current) {
        revealAbortRef.current.aborted = true
        revealAbortRef.current = null
      }

      isAwaitingRevealRef.current = false

      resizeObserver?.disconnect()
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
    }
  }, [activationKey, enabled, forceScrollToTarget, scrollTargetId, targetId])

  return bounds
}
